// src/pages/CaseStudy/SoftwareIntegration.jsx
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Close,
  ArrowForward,
  CheckCircle,
  Phone,
  Email,
  Code,
  Gamepad,
  Security,
  HealthAndSafety,
  School,
  AttachMoney,
  Share,
  Facebook,
  Twitter,
  LinkedIn,
  WhatsApp,
  Google,
  Psychology,
  AutoAwesome,
  Devices,
  People,
  Speed,
} from "@mui/icons-material";
import styles from "./SoftwareIntegration.module.css";

// Case Study Data
const caseStudies = [
  {
    id: 1,
    title: "MEDMEAW - Health Data Platform",
    category: "Health & Data Analytics",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop",
    icon: <HealthAndSafety sx={{ fontSize: 40 }} />,
    description:
      "MEDMEAW is a health data platform uploaded with verified user data and contents with user consent. Data is processed based on algorithms to give best information.",
    challenge:
      "Create a secure health data platform that processes user data with advanced algorithms while ensuring privacy and consent.",
    solution:
      "Developed MEDMEAW with secure data processing algorithms and integrated social media sharing capabilities for Facebook, Twitter, Google Plus, LinkedIn, and WhatsApp.",
    results:
      "Successfully launched with 1000+ users. Enhanced data processing efficiency by 40% through algorithm optimization.",
    features: [
      "User Data Processing",
      "Advanced Algorithms",
      "Social Media Integration",
      "Data Privacy & Security",
      "Real-time Analytics",
    ],
    platforms: ["Facebook", "Twitter", "Google+", "LinkedIn", "WhatsApp"],
    stats: [
      { label: "Active Users", value: "1000+", icon: <People /> },
      { label: "Data Accuracy", value: "96%", icon: <CheckCircle /> },
      { label: "Processing Speed", value: "40% Faster", icon: <Speed /> },
    ],
    color: "#8cc61d",
  },
  {
    id: 2,
    title: "Educational VR Game - Autism Support",
    category: "Education & VR",
    image:
      "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?w=600&h=400&fit=crop",
    icon: <Psychology sx={{ fontSize: 40 }} />,
    description:
      "Educational Virtual Reality Game designed to aid autistic children of various bands with their mental development and coordination.",
    challenge:
      "Develop an immersive VR experience that helps autistic children with mental development and coordination in an engaging way.",
    solution:
      "Created a VR game with interactive exercises focused on cognitive development, coordination, and social skills for children with autism.",
    results:
      "Successfully deployed in 20+ special education centers. Parents reported 30% improvement in coordination and engagement.",
    features: [
      "VR Immersive Experience",
      "Cognitive Development",
      "Coordination Training",
      "Social Skills",
      "Progress Tracking",
    ],
    platforms: ["VR Headsets", "Mobile Devices", "Tablets"],
    stats: [
      { label: "Education Centers", value: "20+", icon: <School /> },
      { label: "Improvement Rate", value: "30%", icon: <AutoAwesome /> },
      { label: "User Engagement", value: "85%", icon: <Gamepad /> },
    ],
    color: "#4a90d9",
  },
  {
    id: 3,
    title: "Chess Game - Primary School Education",
    category: "Educational Gaming",
    image:
      "https://images.unsplash.com/photo-1560174038-da43ac74f01b?w=600&h=400&fit=crop",
    icon: <Gamepad sx={{ fontSize: 40 }} />,
    description:
      "A chess game designed and developed for primary school children to enhance strategic thinking and cognitive development.",
    challenge:
      "Create an engaging chess game that is educational and age-appropriate for primary school children.",
    solution:
      "Developed a colorful, interactive chess game with tutorials, difficulty levels, and progress tracking for young learners.",
    results:
      "Adopted by 15+ primary schools. Children showed improved strategic thinking and problem-solving skills.",
    features: [
      "Interactive Chessboard",
      "Tutorial Mode",
      "Multiple Difficulty Levels",
      "Progress Tracking",
      "Parent Dashboard",
    ],
    platforms: ["Web", "Mobile", "Tablet"],
    stats: [
      { label: "Schools Using", value: "15+", icon: <School /> },
      { label: "Student Engagement", value: "90%", icon: <Gamepad /> },
      { label: "Skill Improvement", value: "25%", icon: <AutoAwesome /> },
    ],
    color: "#f39c12",
  },
  {
    id: 4,
    title: "Finance & Security Solutions",
    category: "Finance & Security",
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop",
    icon: <Security sx={{ fontSize: 40 }} />,
    description:
      "Comprehensive finance and security software solutions for businesses, including secure transaction processing and data protection.",
    challenge:
      "Develop robust finance and security software that ensures data protection and seamless transaction processing.",
    solution:
      "Created integrated solutions with advanced encryption, secure payment gateways, and real-time fraud detection.",
    results:
      "Protected 500+ financial transactions daily. Achieved 99.9% security compliance rate.",
    features: [
      "Encryption & Security",
      "Fraud Detection",
      "Payment Gateways",
      "Data Protection",
      "Compliance Monitoring",
    ],
    platforms: ["Web", "Mobile", "Desktop"],
    stats: [
      { label: "Daily Transactions", value: "500+", icon: <AttachMoney /> },
      { label: "Security Compliance", value: "99.9%", icon: <Security /> },
      { label: "Client Satisfaction", value: "94%", icon: <CheckCircle /> },
    ],
    color: "#e74c3c",
  },
  {
    id: 5,
    title: "Training & Development Platform",
    category: "Training & Development",
    image:
      "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=600&h=400&fit=crop",
    icon: <School sx={{ fontSize: 40 }} />,
    description:
      "Comprehensive training and development platform for corporate and educational institutions with integrated learning management.",
    challenge:
      "Create a scalable learning platform that supports multiple training formats and tracks progress.",
    solution:
      "Developed an LMS with interactive content, assessments, and detailed analytics for trainers and administrators.",
    results:
      "Successfully onboarded 50+ organizations. 80% increase in training completion rates.",
    features: [
      "Learning Management",
      "Interactive Content",
      "Progress Tracking",
      "Analytics Dashboard",
      "Certification",
    ],
    platforms: ["Web", "Mobile", "LMS Integration"],
    stats: [
      { label: "Organizations", value: "50+", icon: <People /> },
      { label: "Completion Rate", value: "80%", icon: <AutoAwesome /> },
      { label: "User Satisfaction", value: "4.8", icon: <CheckCircle /> },
    ],
    color: "#9b59b6",
  },
];

// Categories
const categories = [
  { id: "all", label: "All Projects", icon: "📱" },
  { id: "Health & Data Analytics", label: "Health & Data", icon: "🏥" },
  { id: "Education & VR", label: "Education & VR", icon: "🎓" },
  { id: "Educational Gaming", label: "Educational Gaming", icon: "🎮" },
  { id: "Finance & Security", label: "Finance & Security", icon: "🔒" },
  { id: "Training & Development", label: "Training & Dev", icon: "📚" },
];

// Stats Data
const statsData = [
  { number: "50+", label: "Projects Delivered", icon: <Code /> },
  { number: "30+", label: "Happy Clients", icon: <People /> },
  { number: "4.9", label: "Average Rating", icon: <CheckCircle /> },
  { number: "100%", label: "Client Retention", icon: <AutoAwesome /> },
];

const SoftwareIntegration = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState(null);

  // Filter case studies based on selected category
  const filteredStudies =
    selectedCategory === "all"
      ? caseStudies
      : caseStudies.filter((study) => study.category === selectedCategory);

  const handleOpenDialog = (study) => {
    setSelectedStudy(study);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedStudy(null);
  };

  return (
    <Box className={styles.caseStudyPage}>
      {/* ===== HERO SECTION WITH BACKGROUND IMAGE & ANIMATIONS ===== */}
      <Box className={styles.heroSection}>
        {/* Animated Background Elements */}
        <Box className={styles.animatedBg}>
          {/* Floating Particles */}
          <Box className={`${styles.particle} ${styles.particle1}`}></Box>
          <Box className={`${styles.particle} ${styles.particle2}`}></Box>
          <Box className={`${styles.particle} ${styles.particle3}`}></Box>
          <Box className={`${styles.particle} ${styles.particle4}`}></Box>
          <Box className={`${styles.particle} ${styles.particle5}`}></Box>
          <Box className={`${styles.particle} ${styles.particle6}`}></Box>
          <Box className={`${styles.particle} ${styles.particle7}`}></Box>
          <Box className={`${styles.particle} ${styles.particle8}`}></Box>

          {/* Floating Software Icons */}
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon1}`}>
            💻
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon2}`}>
            ⚡
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon3}`}>
            🎮
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon4}`}>
            🔒
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon5}`}>
            🏥
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon6}`}>
            📚
          </Box>

          {/* Floating Shapes */}
          <Box className={`${styles.shape} ${styles.shape1}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape2}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape3}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>

          {/* Floating Lines */}
          <Box className={`${styles.line} ${styles.line1}`}></Box>
          <Box className={`${styles.line} ${styles.line2}`}></Box>

          {/* Code Pattern Dots */}
          <Box className={styles.codeDots}>
            <Box className={styles.codeDot}>&lt;/&gt;</Box>
            <Box className={styles.codeDot}>{"{ }"}</Box>
            <Box className={styles.codeDot}>&lt; / &gt;</Box>
            <Box className={styles.codeDot}>{"() =>"}</Box>
            <Box className={styles.codeDot}>&lt; &gt;</Box>
            <Box className={styles.codeDot}>{"//"}</Box>
            <Box className={styles.codeDot}>{"[]"}</Box>
            <Box className={styles.codeDot}>{"..."}</Box>
            <Box className={styles.codeDot}>{"*"}</Box>
            <Box className={styles.codeDot}>{"#"}</Box>
            <Box className={styles.codeDot}>{"$"}</Box>
            <Box className={styles.codeDot}>{"@"}</Box>
          </Box>
        </Box>

        <Container maxWidth="lg">
          <Box className={styles.heroContent}>
            <Typography
              variant="h1"
              className={styles.heroTitle}
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
              }}
            >
              Apps Development &{" "}
              <span className={styles.highlight}>Software Integration</span>
            </Typography>
            <Typography variant="h6" className={styles.heroSubtitle}>
              Innovative software solutions for gaming, finance, security,
              health, and training
            </Typography>

            {/* Scroll Indicator */}
            <Box className={styles.scrollIndicator}>
              <Box className={styles.scrollMouse}>
                <Box className={styles.scrollWheel}></Box>
              </Box>
              <Typography variant="caption" className={styles.scrollText}>
                Scroll to explore
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Intro Section */}
      <Box className={styles.introSection}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Box className={styles.introContent}>
                <Chip
                  label="💡 Software Solutions"
                  className={styles.sectionChip}
                  size="small"
                />
                <Typography
                  variant="h2"
                  className={styles.sectionTitle}
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
                  }}
                >
                  <span className={styles.highlight}>Innovative</span> Software
                  Development
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We specialize in developing cutting-edge software solutions
                  across multiple domains including{" "}
                  <strong>
                    Games, Finance, Security, Health, and Training
                  </strong>
                  .
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  Our team delivers integrated software solutions that are
                  scalable, secure, and designed to meet the unique needs of our
                  clients.
                </Typography>
                <Box className={styles.domainBox}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    className={styles.domainTitle}
                  >
                    Our Key Domains:
                  </Typography>
                  <Box className={styles.domainChips}>
                    <Chip label="🎮 Games" className={styles.domainChip} />
                    <Chip label="💰 Finance" className={styles.domainChip} />
                    <Chip label="🔒 Security" className={styles.domainChip} />
                    <Chip label="🏥 Health" className={styles.domainChip} />
                    <Chip label="📚 Training" className={styles.domainChip} />
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box className={styles.statsWrapper}>
                <Paper elevation={0} className={styles.statsPaper}>
                  <Grid container spacing={2}>
                    {statsData.map((stat, index) => (
                      <Grid size={{ xs: 6, sm: 6 }} key={index}>
                        <Box className={styles.statCard}>
                          <Box className={styles.statIcon}>{stat.icon}</Box>
                          <Typography
                            variant="h3"
                            className={styles.statNumber}
                          >
                            {stat.number}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={styles.statLabel}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Case Studies Section */}
      <Box className={styles.caseStudiesSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="📋 Our Projects"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Featured <span className={styles.highlight}>Case Studies</span>
            </Typography>
          </Box>

          {/* Category Filters */}
          <Box className={styles.categoryFilters}>
            {categories.map((category) => (
              <button
                key={category.id}
                className={`${styles.categoryBtn} ${
                  selectedCategory === category.id
                    ? styles.categoryBtnActive
                    : ""
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className={styles.categoryIcon}>{category.icon}</span>
                {category.label}
              </button>
            ))}
          </Box>

          {/* Case Studies Grid */}
          <Grid container spacing={4}>
            {filteredStudies.map((study) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={study.id}>
                <Card className={styles.caseCard}>
                  <Box className={styles.cardImageWrapper}>
                    <Box
                      component="img"
                      src={study.image}
                      alt={study.title}
                      className={styles.cardImage}
                    />
                    <Box className={styles.cardBadge}>
                      <Chip
                        label={study.category}
                        size="small"
                        className={styles.categoryBadge}
                        sx={{
                          background: `${study.color}20`,
                          color: study.color,
                        }}
                      />
                    </Box>
                    <Box
                      className={styles.cardIcon}
                      sx={{ color: study.color }}
                    >
                      {study.icon}
                    </Box>
                  </Box>
                  <CardContent className={styles.cardContent}>
                    <Typography variant="h6" className={styles.cardTitle}>
                      {study.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.cardDescription}
                    >
                      {study.description.length > 100
                        ? `${study.description.substring(0, 100)}...`
                        : study.description}
                    </Typography>
                    <Box className={styles.cardStats}>
                      {study.stats.slice(0, 2).map((stat, idx) => (
                        <Box key={idx} className={styles.cardStat}>
                          <Box
                            className={styles.cardStatIcon}
                            sx={{ color: study.color }}
                          >
                            {stat.icon}
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              className={styles.cardStatValue}
                            >
                              {stat.value}
                            </Typography>
                            <Typography
                              variant="caption"
                              className={styles.cardStatLabel}
                            >
                              {stat.label}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    {study.platforms && (
                      <Box className={styles.cardPlatforms}>
                        {study.platforms.slice(0, 3).map((platform, idx) => (
                          <Chip
                            key={idx}
                            label={platform}
                            size="small"
                            className={styles.platformChip}
                          />
                        ))}
                      </Box>
                    )}
                    <Button
                      variant="contained"
                      className={styles.viewBtn}
                      onClick={() => handleOpenDialog(study)}
                      endIcon={<ArrowForward />}
                      fullWidth
                      sx={{ background: study.color }}
                    >
                      View Case Study
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filteredStudies.length === 0 && (
            <Box className={styles.noResults}>
              <Typography variant="h6">
                No case studies found in this category
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Try selecting a different category
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* CTA Section */}
      <Box className={styles.ctaSection}>
        <Container maxWidth="lg">
          <Box className={styles.ctaContent}>
            <Typography variant="h3" className={styles.ctaTitle}>
              Ready to Build Your{" "}
              <span className={styles.highlight}>Software Solution</span>?
            </Typography>
            <Typography variant="body1" className={styles.ctaText}>
              Let's create innovative software that will transform your business
              and drive growth.
            </Typography>
            <Box className={styles.ctaButtons}>
              <Button
                variant="contained"
                className={styles.ctaPrimaryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
              <Button
                variant="outlined"
                className={styles.ctaSecondaryBtn}
                startIcon={<Email />}
              >
                Email Us
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Case Study Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        className={styles.dialog}
      >
        {selectedStudy && (
          <>
            <DialogTitle className={styles.dialogTitle}>
              <Box className={styles.dialogHeader}>
                <Box
                  className={styles.dialogIcon}
                  sx={{
                    background: `${selectedStudy.color}15`,
                    color: selectedStudy.color,
                  }}
                >
                  {selectedStudy.icon}
                </Box>
                <Typography variant="h5" className={styles.dialogTitleText}>
                  {selectedStudy.title}
                </Typography>
                <IconButton
                  onClick={handleCloseDialog}
                  className={styles.dialogClose}
                >
                  <Close />
                </IconButton>
              </Box>
              <Chip
                label={selectedStudy.category}
                size="small"
                className={styles.dialogCategoryChip}
                sx={{
                  background: `${selectedStudy.color}20`,
                  color: selectedStudy.color,
                }}
              />
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="body1"
                    className={styles.dialogDescription}
                  >
                    {selectedStudy.description}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box className={styles.dialogSection}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      className={styles.dialogSectionTitle}
                    >
                      🎯 Challenge
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.dialogSectionText}
                    >
                      {selectedStudy.challenge}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box className={styles.dialogSection}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      className={styles.dialogSectionTitle}
                    >
                      💡 Solution
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.dialogSectionText}
                    >
                      {selectedStudy.solution}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box className={styles.dialogSection}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      className={styles.dialogSectionTitle}
                    >
                      📊 Results
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.dialogSectionText}
                    >
                      {selectedStudy.results}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider className={styles.dialogDivider} />
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    className={styles.dialogFeaturesTitle}
                  >
                    Key Features:
                  </Typography>
                  <Box className={styles.dialogFeaturesList}>
                    {selectedStudy.features.map((feature, index) => (
                      <Box key={index} className={styles.dialogFeature}>
                        <CheckCircle
                          className={styles.dialogFeatureCheck}
                          sx={{ color: selectedStudy.color }}
                        />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
                {selectedStudy.platforms && (
                  <Grid size={{ xs: 12 }}>
                    <Divider className={styles.dialogDivider} />
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      className={styles.dialogPlatformsTitle}
                    >
                      📱 Platforms:
                    </Typography>
                    <Box className={styles.dialogPlatforms}>
                      {selectedStudy.platforms.map((platform, index) => (
                        <Chip
                          key={index}
                          label={platform}
                          className={styles.dialogPlatformChip}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
                className={styles.dialogCancelBtn}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className={styles.dialogInquiryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SoftwareIntegration;
